import {compactVerify, importJWK} from 'jose';

export type DayNames = {
    aDay: string;
    bDay: string;
};

export type SemesterMatrix = {
    semesterOneDayA: string;
    semesterOneDayB: string;
    semesterTwoDayA: string;
    semesterTwoDayB: string;
};

type Pass = {
    borderColor: string;
    fillColor: string;
    textColor: string;
    text: string;
};

type CommonIdCard = {
    id: string;
    name: string;
    backgroundImage: string;
    textColor: string;
    schoolBanner: string;
    photo: string;
    type: 'student' | 'staff';
    expiresAt?: number;
};

type HelpText = {
    header: string;
    body: string;
};

export type StudentIdCard = CommonIdCard & {
    type: 'student';
    schoolYear: string;
    grade: string;
    instructionText: string;
    helpText: HelpText[] | HelpText;
    activityPass: Pass | null;
    busPass: Pass | null;
    dayNames: DayNames;
    lunchPeriods?: SemesterMatrix;
    lateArrival: SemesterMatrix;
    earlyRelease: SemesterMatrix;
};

type DirectoryBlock = {
    header: string;
    body: string;
};

export type StaffIdCard = CommonIdCard & {
    type: 'staff';
    directory: DirectoryBlock[];
};

type PublicKey = {
    alg: string;
    e: string;
    kid: string;
    kty: string;
    n: string;
    use: string;
};

export type IdCard = StudentIdCard | StaffIdCard;
const publicKey = JSON.parse(window.atob(import.meta.env.VITE_ID_CARD_PUBLIC_KEY)) as PublicKey;

const verifyIdCard = async (rawIdCard: string): Promise<IdCard> => {
    try {
        const {payload, protectedHeader} = await compactVerify(
            rawIdCard,
            await importJWK(publicKey),
            {
                algorithms: ['RS256'],
            },
        );

        if (protectedHeader.kid !== 'id-card') {
            throw new Error('Could not verify id card.');
        }

        const response = new TextDecoder().decode(payload);
        return JSON.parse(response) as IdCard;
    } catch (e: unknown) {
        console.log('e', e);
        throw e;
    }
};

export const isIdCardExpired = (idCard: IdCard): boolean => {
    return !idCard.expiresAt || idCard.expiresAt < Math.floor(Date.now() / 1000);
};

export const storeIdCard = async (rawIdCard: string): Promise<IdCard> => {
    const idCard = await verifyIdCard(rawIdCard);
    localStorage.setItem('id-card', rawIdCard);
    return idCard;
};

export const getIdCard = async (): Promise<IdCard> => {
    const rawIdCard = localStorage.getItem('id-card');

    if (!rawIdCard) {
        throw new Error('No ID card stored');
    }

    return verifyIdCard(rawIdCard);
};

export const clearIdCard = (): void => {
    localStorage.removeItem('id-card');
};
